import { StorageKeys } from "./Authentication";
import authService from "./AuthService";
import { getSessionMetadata } from "./Helpers";

const isDev = process.env.NODE_ENV === "development";
const isUseLocalEnvironment = process.env.REACT_APP_USE_LOCAL_ENV === "true";

export const BaseUri = isUseLocalEnvironment
  ? "http://localhost:3000/api/portal/"
  : isDev
  ? "https://dev-keychain.medchart.com/portal/"
  : process.env.REACT_APP_API_BASE_URI!;

export const PublicBaseUri = BaseUri.replace("/portal/", "/public-portal/");
export const OBOHeaderKey = "OBO-Life-Id";

export enum MethodEnum {
  Get = "GET",
  Post = "POST",
  Put = "PUT",
  Patch = "PATCH",
  Delete = "DELETE",
}

export interface IApiSuccess<T> {
  ok: true;
  payload: T;
}

export interface IError {
  code: string;
  message: string;
  field: string;
}

export interface IApiError {
  ok: false;
  status: number;
  errors: Array<IError>;
  callbackUri?: string;
  appName?: string;
}

export async function publicApi<T>(
  path: string,
  method = MethodEnum.Get,
  body: any = null
) {
  return executeCallFor<T>(getUri(PublicBaseUri, path), method, body);
}

export async function api<T>(
  path: string,
  method = MethodEnum.Get,
  body: any = null,
  excludeOboHeader: boolean = false
) {
  return executeCallFor<T>(
    getUri(BaseUri, path),
    method,
    body,
    excludeOboHeader
  );
}

export async function apiBlob(
  path: string,
  method = MethodEnum.Get,
  body: any = null
) {
  return await executeCallForBlob(getUri(BaseUri, path), method, body);
}

export async function apiAuthorizations<T>(
  path: string,
  method = MethodEnum.Get,
  body: any = null
) {
  return executeCallFor<T>(
    getUri(`${BaseUri}authorizations/`, path),
    method,
    body
  );
}

export async function apiAuthorizationsBlob(
  path: string,
  method = MethodEnum.Get,
  body: any = null
) {
  return await executeCallForBlob(
    getUri(`${BaseUri}authorizations/`, path),
    method,
    body
  );
}

export async function apiLives<T>(
  path: string = "",
  method = MethodEnum.Get,
  body: any = null
) {
  return executeCallFor<T>(getUri(`${BaseUri}lives/`, path), method, body);
}

export async function apiRetrievals<T>(
  path: string = "",
  method = MethodEnum.Get,
  body: any = null
) {
  return executeCallFor<T>(getUri(`${BaseUri}retrievals/`, path), method, body);
}
export async function apiProviders<T>(
  path: string = "",
  method = MethodEnum.Get,
  body: any = null
) {
  return executeCallFor<T>(getUri(`${BaseUri}providers/`, path), method, body);
}

export async function apiSignatures<T>(
  path: string,
  method = MethodEnum.Get,
  body: any = null
) {
  return executeCallFor<T>(getUri(`${BaseUri}signatures/`, path), method, body);
}

export async function apiSignaturesBlob(
  path: string,
  method = MethodEnum.Get,
  body: any = null
) {
  return await executeCallForBlob(
    getUri(`${BaseUri}signatures/`, path),
    method,
    body
  );
}

export async function apiData<T>(
  path: string = "",
  method = MethodEnum.Get,
  body: any = null
) {
  return await executeCallFor<T>(getUri(`${BaseUri}data/`, path), method, body);
}

export async function apiApps<T>(
  path: string = "",
  method = MethodEnum.Get,
  body: any = null
) {
  return await executeCallFor<T>(getUri(`${BaseUri}apps/`, path), method, body);
}

function getUri(baseUri: string, path: string) {
  const uri = new URL(`${baseUri}${path}`);
  uri.searchParams.append("api-version", "21.09");
  return uri.toString();
}

export async function executeCallFor<T>(
  url: string,
  method: MethodEnum = MethodEnum.Get,
  body: any = null,
  excludeOboHeader: boolean = false
): Promise<IApiSuccess<T> | IApiError | undefined> {
  try {
    const response = await fetchWithAuth(url, method, body, excludeOboHeader);
    if (response.ok) {
      return {
        ok: true,
        payload: (response.status === 204 ? {} : await response.json()) as T,
      };
    }
    return processCallError(response);
  } catch (e) {
    console.error(e);
  }
}

export async function executeCallForBlob(
  url: string = "",
  method: MethodEnum = MethodEnum.Get,
  body: any = null,
  excludeOboHeader: boolean = false,
  progressCallback?: (bytesReceived: number, totalBytes: number) => void
): Promise<IApiSuccess<File> | IApiError | undefined> {
  try {
    const response = await fetchStreamWithAuth(
      url,
      method,
      body,
      excludeOboHeader,
      progressCallback
    );
    if (!(response instanceof Response)) {
      return {
        ok: true,
        payload: new File([response.blob], response.fileName, {
          type: response.mimeType,
        }),
      };
    }
    return processCallError(response);
  } catch (e) {
    console.error(e);
  }
}

async function processCallError(
  response: Response
): Promise<IApiError | undefined> {
  if (response.status === 400) {
    const responseString: any = await response.text();
    const responseAsJson = responseString && JSON.parse(responseString);
    return {
      ok: false,
      status: 400,
      errors: responseAsJson?.errors,
      callbackUri: responseAsJson?.callbackUri,
      appName: responseAsJson?.appName,
    };
  } else if (response.status === 401) {
    // force logout if unauthorized
    authService.logout();
  }
  return {
    ok: false,
    status: response.status,
    errors: [],
  };
}

async function fetchWithAuth(
  url: string,
  method: MethodEnum = MethodEnum.Get,
  body: any = null,
  excludeOboHeader: boolean = false
) {
  const signature = authService.getAccessToken();
  const newDate = new Date();

  let headers: any = {
    "Content-Type": "application/json",
    "X-Timezone-Offset": newDate.getTimezoneOffset().toString(),
    "auth-sig": signature ?? "",
    ...(!excludeOboHeader && getSessionMetadata()?.oboId
      ? { [OBOHeaderKey]: getSessionMetadata()?.oboId }
      : undefined),
  };

  if (body instanceof FormData) {
    delete headers["Content-Type"];
  }

  if (!excludeOboHeader) {
    // Used for dedicated portal
    const managedAccountLifeId = sessionStorage.getItem(
      StorageKeys.ManagedAccountLifeId
    );
    if (managedAccountLifeId && managedAccountLifeId !== "") {
      headers[OBOHeaderKey] = managedAccountLifeId;
    }
  }
  
  return await fetch(url, {
    method: method,
    headers: headers,
    body:
      body && body instanceof FormData
        ? body
        : body && !(body instanceof FormData)
        ? JSON.stringify(body)
        : null,
    credentials: "include",
  });
}

const fetchStreamWithAuth = (
  url: string,
  method: MethodEnum = MethodEnum.Get,
  body: any = null,
  excludeOboHeader: boolean = false,
  progressCallback?: (bytesReceived: number, totalBytes: number) => void
) => {
  return fetchWithAuth(url, method, body, excludeOboHeader)
    .then((response) => {
      if (response.ok) {
        const reader = response.body!.getReader();
        const totalBytes = parseInt(
          response.headers.get("Content-Length") || "0"
        );
        let transferredBytes = 0;
        return {
          fileName: response.headers
            .get("content-disposition")
            ?.split(";")[1]
            .split("=")[1],
          mimeType: response.headers.get("content-type"),
          stream: new ReadableStream({
            start(controller) {
              return pump();
              function pump(): any {
                return reader.read().then(({ done, value }) => {
                  // When no more data needs to be consumed, close the stream
                  if (done) {
                    controller.close();
                    return;
                  }
                  // Enqueue the next data chunk into our target stream
                  controller.enqueue(value);
                  transferredBytes += value!.length;
                  if (progressCallback) {
                    progressCallback(transferredBytes, totalBytes);
                  }
                  return pump();
                });
              }
            },
          }),
        };
      } else {
        return response;
      }
    })
    .then(async (i: any) => {
      if (i instanceof Response) {
        return i;
      } else {
        return { ...i, blob: await new Response(i.stream).blob() };
      }
    });
};
